import { Box, Button, Drawer, FormControl, IconButton, TextField, TextareaAutosize, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';
import { setWallet } from '../../store/crm/crm';
import { useFormik } from 'formik';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CustomButtoncolor from '../custom-button/customColorButton';
import Waveloading from '../../pages/loading/Waveloading';
import { NOTE_MAX_LENGTH, noteLimitError } from '../../utils/noteLimits';

const BURGUNDY = '#A72C68';
const BURGUNDY_DARK = '#921F58';
const PINK_BG = '#FAEFF5';
const PINK_BORDER = '#F0CDDB';

const labelStyle = {
    color: BURGUNDY,
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    mb: '6px',
};

export const inputStyle = {
    background: '#fff',
    borderRadius: '10px',
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        '& fieldset': { borderColor: PINK_BORDER },
        '&:hover fieldset': { borderColor: BURGUNDY },
        '&.Mui-focused fieldset': { borderColor: BURGUNDY, borderWidth: '1.5px' },
    },
};

export const textareaStyle = {
    width: '100%',
    resize: 'vertical',
    padding: '10px 12px',
    border: 'none',
    outline: 'none',
    borderRadius: '10px',
    fontFamily: 'inherit',
    fontSize: '14px',
    color: '#1a1a2e',
    background: 'transparent',
    boxSizing: 'border-box',
    minHeight: '120px',
};

const SetWalletModal = ({ customer_id, reFetchData }) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const validate = (values) => {
        const errors = {};
        if (!values.passcode) errors.passcode = 'Passcode is required';
        if (!values.waletnotes) errors.waletnotes = 'Wallet Notes is required';
        if (!values.wallet_points) errors.wallet_points = 'Wallet points are required';
        return errors;
    };

    const formik = useFormik({
        initialValues: { passcode: '', waletnotes: '', wallet_points: '' },
        validate,
        onSubmit: async (values) => {
            try {
                const finalPayload = {
                    customer_id: customer_id,
                    passcode: values.passcode,
                    notes: values.waletnotes,
                    wallet_points: values.wallet_points,
                };
                setLoading(true);
                let { payload } = await dispatch(setWallet(finalPayload));
                if (payload?.success === 'true') {
                    toast.success('Wallet update successfully!');
                    reFetchData && reFetchData();
                    setLoading(false);
                    handleClose();
                } else {
                    setLoading(false);
                    if (payload?.data?.errors) {
                        toast.error(payload?.data?.errors || 'Something went wrong.');
                    }
                }
            } catch (error) {
                setLoading(false);
            }
        },
    });

    return (
        <>
            <CustomButtoncolor
                handleClick={handleOpen}
                mainColor={'#219653'}
                size="md"
                val="Set wallet"
                style={{
                    padding: '7px 5px',
                    fontSize: '18px',
                    minWidth: 'max-content',
                    borderRadius: '6px!important',
                    width: '100%',
                    '@media (minWidth: 420px)': { width: 'auto' },
                    '@media (max-width:319px)': { fontSize: '15px' },
                }}
            />

            <Drawer
                anchor="right"
                open={open}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 480, md: 540 },
                        maxWidth: '100vw',
                        background: '#fff',
                        boxShadow: '-12px 0 40px rgba(0,0,0,0.10)',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                {/* Header */}
                <Box sx={{
                    position: 'relative',
                    display: 'flex', alignItems: 'flex-start', gap: 1.25,
                    px: 2.5, py: 2,
                    borderBottom: `1px solid ${PINK_BORDER}`,
                    flexShrink: 0,
                }}>
                    <Box sx={{
                        width: 42, height: 42, borderRadius: '10px',
                        background: PINK_BG, border: `1px solid ${PINK_BORDER}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <AccountBalanceWalletOutlinedIcon sx={{ color: BURGUNDY, fontSize: 22 }} />
                    </Box>
                    <Box sx={{ flex: 1, pr: 3 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '17px', color: '#1a1a2e', lineHeight: 1.2, fontFamily: 'helvetica-lt-std-bold' }}>
                            Change Wallet Point
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#6B7280', mt: '2px' }}>
                            Adjust the customer's wallet balance and add a note.
                        </Typography>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={handleClose}
                        sx={{
                            position: 'absolute', top: 10, right: 10,
                            color: '#6B7280', borderRadius: '8px',
                            width: 32, height: 32,
                            border: `1px solid ${PINK_BORDER}`,
                            '&:hover': { background: PINK_BG, color: BURGUNDY },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>

                {/* Body */}
                <Box sx={{ flex: 1, overflowY: 'auto', position: 'relative', p: { xs: 2, sm: 3 } }}>
                    {loading && <Waveloading cover />}

                    <FormControl fullWidth sx={{ mb: 2.5 }}>
                        <Typography sx={labelStyle}>Passcode</Typography>
                        <TextField
                            fullWidth
                            size="small"
                            name="passcode"
                            sx={inputStyle}
                            placeholder="Enter passcode"
                            value={formik.values.passcode}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        />
                        {formik.touched.passcode && formik.errors.passcode && (
                            <Typography sx={{ color: '#CB3838', fontSize: '12px', mt: 0.5 }}>{formik.errors.passcode}</Typography>
                        )}
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 2.5 }}>
                        <Typography sx={labelStyle}>Wallet Points</Typography>
                        <TextField
                            fullWidth
                            size="small"
                            name="wallet_points"
                            sx={inputStyle}
                            placeholder="Enter wallet points"
                            value={formik.values.wallet_points}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        />
                        {formik.touched.wallet_points && formik.errors.wallet_points && (
                            <Typography sx={{ color: '#CB3838', fontSize: '12px', mt: 0.5 }}>{formik.errors.wallet_points}</Typography>
                        )}
                    </FormControl>

                    <FormControl fullWidth>
                        <Typography sx={labelStyle}>Wallet Notes</Typography>
                        <Box sx={{
                            position: 'relative',
                            border: `1px solid ${PINK_BORDER}`,
                            borderRadius: '10px',
                            background: '#fff',
                            transition: 'border-color 0.15s',
                            '&:focus-within': { borderColor: BURGUNDY, boxShadow: '0 0 0 2px rgba(167,44,104,0.10)' },
                        }}>
                            <TextareaAutosize
                                style={textareaStyle}
                                minRows={5}
                                name="waletnotes"
                                maxLength={NOTE_MAX_LENGTH}
                                placeholder="Add a note for this adjustment…"
                                value={formik.values.waletnotes}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            />
                        </Box>
                        {!!noteLimitError(formik.values.waletnotes) && (
                            <Typography sx={{ color: '#EB5757', fontSize: '12px', mt: 0.5 }}>{noteLimitError(formik.values.waletnotes)}</Typography>
                        )}
                        {formik.touched.waletnotes && formik.errors.waletnotes && (
                            <Typography sx={{ color: '#CB3838', fontSize: '12px', mt: 0.5 }}>{formik.errors.waletnotes}</Typography>
                        )}
                    </FormControl>
                </Box>

                {/* Footer */}
                <Box sx={{
                    display: 'flex', justifyContent: 'flex-end', gap: 1,
                    px: { xs: 2, sm: 3 }, py: 2,
                    borderTop: `1px solid ${PINK_BORDER}`,
                    flexShrink: 0,
                }}>
                    <Button
                        onClick={handleClose}
                        sx={{
                            height: 42, px: 3, borderRadius: '10px',
                            textTransform: 'capitalize', fontSize: '13px', fontWeight: 600,
                            color: '#fff', backgroundColor: '#EB5757',
                            border: '1px solid #EB5757',
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: '#fff', color: '#EB5757', border: '1px solid #EB5757', boxShadow: 'none' },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={formik.handleSubmit}
                        sx={{
                            height: 42, px: 3, borderRadius: '10px',
                            textTransform: 'capitalize', fontSize: '13px', fontWeight: 700,
                            color: '#fff',
                            background: 'linear-gradient(135deg, #27AE60 0%, #1E8449 100%)',
                            border: '1px solid #27AE60',
                            boxShadow: 'none',
                            '&:hover': { background: '#219653', color: '#fff', border: '1px solid #27AE60', boxShadow: 'none' },
                        }}
                    >
                        Save
                    </Button>
                </Box>
            </Drawer>
        </>
    );
};

export default SetWalletModal;
